import { useState } from 'react'
import { usePedirLote, usePreviaDoLote } from '../../../api/queries/loteDeAnalise'
import { Botao } from '../../../components/Botao'
import { AvisoDeErro, Esqueleto } from '../../../components/Estados'
import { CampoDeTexto, CampoDeTextoLongo } from '../../../components/campos'
import { TEXTOS } from '../../../lib/glossario'
import { competenciaPorExtenso } from '../../chamados/competencia'
import { emMoeda, emUnidades } from '../credito-da-ia/dinheiro'

interface Props {
  competencia: string
  aoMudarCompetencia: (valor: string) => void
}

const MINIMO_DA_JUSTIFICATIVA = 10

/**
 * A prévia de um mês, antes de confirmar o gasto.
 *
 * **A estimativa é uma FAIXA, e a tela diz isso.** Não há consumo medido neste sistema para apontar
 * um valor típico — as execuções em produção não trazem medição —, e um ponto inventado no meio de
 * uma faixa honesta é pior que a faixa.
 *
 * **Recusa pelo PISO, avisa pelo TETO.** Se nem o melhor caso cabe, o botão não habilita e a tela diz
 * o que fazer. Se o piso cabe e o teto não, ela avisa e deixa seguir: o que já foi analisado fica, e
 * interromper é um clique.
 *
 * @param props a competência escolhida e o que fazer ao trocá-la
 * @returns a prévia
 */
export function PreviaDoLote({ competencia, aoMudarCompetencia }: Props) {
  const t = TEXTOS.loteDeAnalise
  const [justificativa, setJustificativa] = useState('')
  const [confirmacao, setConfirmacao] = useState('')
  const { data: previa, isPending, isError, error } = usePreviaDoLote(competencia)
  const pedir = usePedirLote()

  const justificativaCurta = justificativa.trim().length < MINIMO_DA_JUSTIFICATIVA
  const digitouOMes = confirmacao.trim() === competencia
  const habilitado = Boolean(previa?.podePedir) && !justificativaCurta && digitouOMes && !pedir.isPending

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <h2 className="text-base font-semibold text-slate-900">{t.novo}</h2>

      <div className="mt-3 max-w-xs">
        <CampoDeTexto
          rotulo={t.escolhaOMes}
          type="month"
          value={competencia}
          onChange={(e) => aoMudarCompetencia(e.target.value)}
        />
      </div>

      {competencia.length === 7 && isPending && <Esqueleto linhas={3} />}
      {isError && <AvisoDeErro erro={error} />}

      {previa && (
        <div className="mt-4 space-y-2 text-sm text-slate-800">
          <p>{t.previa.quantos(previa.candidatos, competenciaPorExtenso(competencia))}</p>
          <p>{t.previa.jaTem(previa.jaComParecer)}</p>
          <p className="font-medium">{t.previa.seraoPagos(previa.seraoPagos)}</p>
          <p className="tabular-nums">
            {t.previa.estimativaEmTokens(emUnidades(previa.tokensMin), emUnidades(previa.tokensMax))}
          </p>
          {previa.reaisMin && previa.reaisMax ? (
            <p className="tabular-nums">{t.previa.estimativaEmDinheiro(emMoeda(previa.reaisMin), emMoeda(previa.reaisMax))}</p>
          ) : (
            <p className="text-xs text-slate-600">{t.previa.estimativaSemPreco}</p>
          )}
          <p className="max-w-prose text-xs text-slate-500">{t.previa.faixaSemPontoMedio}</p>

          {previa.jaHaLoteAberto && <Tarja tom="ambar" texto={t.previa.jaHaUm} />}
          {!previa.cabePeloPiso && previa.aviso && <Tarja tom="vermelho" texto={previa.aviso} />}
          {previa.cabePeloPiso && !previa.cabePeloTeto && previa.aviso && <Tarja tom="ambar" texto={previa.aviso} />}
          {previa.seraoPagos === 0 && <Tarja tom="ambar" texto={t.previa.semTrabalho} />}

          <p className="max-w-prose rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
            {t.previa.oQueNaoFaz}
          </p>

          <div className="space-y-3 pt-1">
            <CampoDeTextoLongo
              rotulo={t.previa.justificativa}
              ajuda={t.previa.justificativaAjuda}
              rows={3}
              value={justificativa}
              onChange={(e) => setJustificativa(e.target.value)}
              disabled={!previa.podePedir}
            />
            <div className="max-w-xs">
              <CampoDeTexto
                rotulo={t.previa.digiteParaConfirmar(competencia)}
                value={confirmacao}
                onChange={(e) => setConfirmacao(e.target.value)}
                disabled={!previa.podePedir}
              />
            </div>
            <Botao
              disabled={!habilitado}
              onClick={() =>
                pedir.mutate(
                  { competencia, justificativa: justificativa.trim() },
                  {
                    onSuccess: () => {
                      setJustificativa('')
                      setConfirmacao('')
                    },
                  },
                )
              }
            >
              {t.previa.confirmar(previa.seraoPagos)}
            </Botao>
            {pedir.isError && <AvisoDeErro erro={pedir.error} />}
          </div>
        </div>
      )}
    </section>
  )
}

/** Tarja informativa. Não é `role="alert"`: pausa e "pode não caber" não são falha. */
function Tarja({ tom, texto }: { tom: 'ambar' | 'vermelho'; texto: string }) {
  const cores =
    tom === 'vermelho' ? 'border-red-200 bg-red-50 text-red-900' : 'border-amber-200 bg-amber-50 text-amber-900'
  return <p className={`max-w-prose rounded-lg border px-3 py-2 text-sm ${cores}`}>{texto}</p>
}
